"use client"

import { useEffect, useState } from "react"
import { captureException } from "@/lib/error-reporter"
import { isChunkLoadError, recoverFromChunkError } from "@/lib/chunk-recovery"

// global-error replaces the root layout entirely, so it cannot rely on the
// theme provider, Tailwind design tokens, or shared components — everything
// here is self-contained. Same recovery semantics as ErrorBoundaryUI: probe
// first, and only reload when the server is reachable and nothing critical is
// in flight. See lib/chunk-recovery.ts for the full rule set.
type Phase = "diagnosing" | "reloading" | "prompt" | "offline" | "generic"

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const isChunk = isChunkLoadError(error)
  const [phase, setPhase] = useState<Phase>(isChunk ? "diagnosing" : "generic")

  useEffect(() => {
    let alive = true

    if (!isChunk) {
      captureException(error, { routeTag: "global_error" })
      return
    }

    void recoverFromChunkError(error).then((outcome) => {
      captureException(error, {
        routeTag: "global_error",
        component: "chunk_load_error",
        extra: { recovery_outcome: outcome },
      })
      if (!alive || outcome === "reloading") return
      setPhase(
        outcome === "offline" ? "offline" : outcome === "not-chunk-error" ? "generic" : "prompt"
      )
    })

    return () => {
      alive = false
    }
  }, [error, isChunk])

  const updating = phase === "diagnosing" || phase === "reloading"

  const title = updating
    ? "Updating to the latest version"
    : phase === "offline"
      ? "Connection interrupted"
      : phase === "prompt"
        ? "A new version is available"
        : "Critical System Error"

  const body = updating
    ? "This will only take a moment…"
    : phase === "offline"
      ? "We couldn't reach the server. Check your connection and try again."
      : phase === "prompt"
        ? "This page is running an older version of the app. Reload to continue."
        : "A fatal error prevented the application from loading. Our team has been notified."

  return (
    <html lang="en">
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </head>
      <body className="flex min-h-[100dvh] flex-col items-center justify-center bg-[#09090b] p-4 font-sans text-white">
        <div
          role={updating ? "status" : "alert"}
          aria-live={updating ? "polite" : "assertive"}
          className="mx-auto w-full max-w-sm space-y-5 rounded-2xl border border-white/10 bg-[#18181b] p-4 text-center shadow-2xl sm:p-6 md:p-8"
        >
          {updating ? (
            <div
              className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-white/15 border-t-white"
              aria-hidden="true"
            />
          ) : (
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10">
              <svg
                className="h-8 w-8 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          )}
          <div>
            <h2 className="text-xl font-bold tracking-tight text-white">{title}</h2>
            <p className="mt-2 text-sm text-gray-400">{body}</p>
            {error.digest && phase === "generic" && (
              <p className="mt-1 text-xs text-gray-500">Reference: {error.digest}</p>
            )}
          </div>
          {!updating && (
            <div className="pt-2">
              <button
                onClick={() => window.location.reload()}
                className="h-11 w-full rounded-lg bg-white text-sm font-semibold text-black transition-colors hover:bg-gray-200"
              >
                {phase === "offline" ? "Try Again" : "Reload Page"}
              </button>
            </div>
          )}
        </div>
      </body>
    </html>
  )
}
